import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogTitle,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useLocation } from 'react-router-dom';
import { AccessConfirmationData } from './Security';
import { composeFullname, composeUsername } from '../../../lib/DisplayName';
import { UserSchema } from '../../../definitions/schemas';
import { handleAutofocus } from '../../../lib/TextField';
import { RoomType } from '../../../enums/RoomTypes';
import { useI18n } from '../../../lib/hook/useI18n';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import UnifiedActivityDataConsentCheckbox from './UnifiedActivityDataConsentCheckbox';
import UnifiedConsentIdentityInfoNote from './UnifiedConsentIdentityInfoNote';
import UnifiedSecurityDialogConsent from './UnifiedSecurityDialogConsent';
import { CONSENT } from '../../../enums/RoomConsent';
import { getConsent } from '../../../lib/Consent';
import Analytics from '../../../tools/Analytics';
import { Events, Referrers } from '../../../enums/Analytics';
import { UnifiedConsentType } from '../../../enums/UnifiedConsentType';
import { QUERY_STRING } from '../../../enums/QueryString';
import ThemableButton from '../../../theme/components/Button/ThemableButton';

const useStyles = makeStyles((theme: Theme) => ({
  footer: {
    backgroundColor: theme.palette.grey[100],
  },
}));

interface Props {
  isConsentDeclinedSuppression?: boolean;
  email?: string;
  hasError: boolean;
  loading: boolean;
  onActivityDataConsentDecision?: (accepted: boolean) => void;
  onSubmit: (data: AccessConfirmationData) => void;
  open: boolean;
  owner?: UserSchema;
  requiresEmail?: boolean;
  requiresPasscode?: boolean;
  roomType?: string;
  verifyEmail?: boolean;
}

const UnifiedSecurityDialogContent: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const classes = useStyles();
  const { t } = useI18n();
  const location = useLocation();

  const {
    isConsentDeclinedSuppression = false,
    email: initialEmail,
    hasError,
    loading,
    onActivityDataConsentDecision,
    onSubmit,
    open,
    owner,
    requiresEmail,
    requiresPasscode,
    roomType,
    verifyEmail,
  } = props;

  const [email, setEmail] = useState(initialEmail ?? '');
  const [passcode, setPasscode] = useState('');
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activityDataConsent, setActivityDataConsent] = useState(false);

  const consentViewedTrackedForOpenCycle = useRef(false);

  const validator = new FormValidatorHelper();
  const isContentLink = roomType === RoomType.ContentLink;

  const shouldVerifyEmail = useCallback(
    (): boolean => !!requiresEmail || !!verifyEmail,
    [requiresEmail, verifyEmail]
  );

  // Determine if consent is shown in the dialog
  const isConsentShown = useMemo(
    () => requiresPasscode || shouldVerifyEmail() || !requiresEmail,
    [requiresPasscode, requiresEmail, shouldVerifyEmail]
  );

  // Get referrer from query params to determine consent type
  const getTrackingReferrer = useCallback((): string => {
    const queryParams = new URLSearchParams(location.search);
    const referrerParam = queryParams.get(QUERY_STRING.REFERRER);
    return referrerParam || '';
  }, [location.search]);

  // Determine consent type for tracking
  const getUnifiedConsentType = useCallback((): UnifiedConsentType => {
    const trackingReferrer = getTrackingReferrer();
    const isFromChat =
      trackingReferrer === Referrers.DEAL_ROOMS.ACTIVITY.MESSAGE_INPUT ||
      trackingReferrer === Referrers.DEAL_ROOMS.ACTIVITY.MESSAGE_REPLY ||
      trackingReferrer === Referrers.DEAL_ROOMS.CONVERSATION_MENTION;

    if (isFromChat) {
      return UnifiedConsentType.CONSENT_FROM_CHAT;
    }

    // Passcode only scenario
    if (requiresPasscode && !shouldVerifyEmail()) {
      return UnifiedConsentType.ONLY_CONSENT;
    }

    // Email scenario (with or without passcode)
    if (shouldVerifyEmail()) {
      return UnifiedConsentType.CONSENT_WITH_EMAIL;
    }

    // Default fallback
    return UnifiedConsentType.ONLY_CONSENT;
  }, [getTrackingReferrer, requiresPasscode, shouldVerifyEmail]);

  const needsActivityDataCheckbox =
    isConsentDeclinedSuppression && isConsentShown;

  useEffect(() => {
    if (!isConsentDeclinedSuppression) {
      return;
    }
    if (open) {
      const persisted = getConsent()?.consent;
      setActivityDataConsent(persisted === CONSENT.ACCEPTED);
    }
  }, [open, isConsentDeclinedSuppression]);

  const handleActivityConsentChange = (checked: boolean) => {
    setActivityDataConsent(checked);
  };

  useEffect(() => {
    if (!open) {
      consentViewedTrackedForOpenCycle.current = false;
      return;
    }

    if (isConsentShown && !consentViewedTrackedForOpenCycle.current) {
      consentViewedTrackedForOpenCycle.current = true;
      const consentType = getUnifiedConsentType();
      Analytics.track(Events.DSR_CONSENT_VIEWED, {
        type: consentType,
      });
    }
  }, [open, isConsentShown, getUnifiedConsentType]);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setDirty(true);
  };

  const onPasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasscode(e.target.value.trim());
    setDirty(true);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setSubmitted(true);

    const validatorKey = shouldVerifyEmail()
      ? t('room.welcome.modal.email').toLowerCase()
      : '';
    if (validatorKey) {
      validator.check(validatorKey, email, 'required|email');
    }

    if (requiresPasscode) {
      const passcodeKey = t('room.welcome.modal.passcode').toLowerCase();
      validator.check(passcodeKey, passcode, 'required');
    }

    if (!validator.isValid()) {
      return;
    }

    if (isConsentDeclinedSuppression && needsActivityDataCheckbox) {
      onActivityDataConsentDecision?.(activityDataConsent);
    }

    if (
      isConsentShown &&
      (!isConsentDeclinedSuppression || activityDataConsent)
    ) {
      const consentType = getUnifiedConsentType();
      Analytics.track(Events.DSR_CONSENT_ACCEPTED, {
        type: consentType,
      });
    }

    const data: AccessConfirmationData = {
      email: shouldVerifyEmail() ? email : undefined,
      passcode: requiresPasscode ? passcode : undefined,
    };

    onSubmit(data);
  };

  const renderErrorMessage = (): JSX.Element => {
    const getRequiredFields = (): string[] => {
      const requiredFields: string[] = [];

      if (shouldVerifyEmail()) {
        requiredFields.push(t('room.welcome.modal.email').toLowerCase());
      }

      if (requiresPasscode) {
        requiredFields.push(t('room.welcome.modal.passcode').toLowerCase());
      }

      return requiredFields;
    };

    const fields = getRequiredFields().join(` ${t('room.welcome.modal.or')} `);
    const validateOnlyEmail = !requiresPasscode && shouldVerifyEmail();

    return (
      <Collapse in={hasError}>
        <Alert color="error" severity="error">
          {validateOnlyEmail
            ? t('room.welcome.modal.validation.only_email')
            : t('room.welcome.modal.validation.error_messages', {
                fields,
              })}
        </Alert>
      </Collapse>
    );
  };

  const renderEmail = (): JSX.Element | undefined => {
    if (!shouldVerifyEmail()) {
      return;
    }

    const helperText = verifyEmail ? t('room.pin.modal.helptext') : '';
    const validatorKey = t('room.welcome.modal.email').toLowerCase();
    validator.check(validatorKey, email, 'required|email');
    const error = validator.getError(validatorKey, submitted);

    return (
      <TextField
        color="secondary"
        error={!!error}
        FormHelperTextProps={{
          sx: { marginLeft: 0 },
        }}
        fullWidth
        helperText={error || helperText}
        inputRef={handleAutofocus(!email, dirty)}
        label={t('room.welcome.modal.email')}
        onChange={onEmailChange}
        required
        type="email"
        value={email}
        variant="outlined"
      />
    );
  };

  const renderPasscode = (): JSX.Element | undefined => {
    if (!requiresPasscode) {
      return;
    }

    const validatorKey = t('room.welcome.modal.passcode')
      .toString()
      .toLowerCase();
    validator.check(validatorKey, passcode, 'required');
    const error = validator.getError(validatorKey, submitted);

    return (
      <TextField
        autoFocus={!!email}
        color="secondary"
        error={!!error}
        fullWidth
        helperText={error}
        inputRef={handleAutofocus(!passcode && !shouldVerifyEmail(), dirty)}
        label={t('room.welcome.modal.passcode')}
        onChange={onPasscodeChange}
        required
        value={passcode}
        variant="outlined"
      />
    );
  };

  const renderButtonText = () => {
    // Use "Enter room" for passcode scenarios (unified flow)
    if (requiresPasscode) {
      return t('room.welcome.modal.newer_button');
    }

    if (verifyEmail) {
      return t('room.pin.modal.newer_button');
    }

    if (isContentLink) {
      return t('room.content_link.modal.view_content');
    }

    return t('room.welcome.modal.newer_button');
  };

  const renderTitle = (): React.ReactNode => {
    // All passcode scenarios use same title
    if (requiresPasscode || requiresEmail) {
      return t('room.welcome.modal.title');
    }

    if (isContentLink) {
      return t('room.content_link.modal.title');
    }

    return t('room.welcome.modal.title');
  };

  const renderSubtitle = (): JSX.Element => {
    // Passcode-only scenario
    if (requiresPasscode && !shouldVerifyEmail()) {
      return (
        <Typography color="textSecondary" variant="body2">
          {t('room.welcome.modal.passcode_only.subtitle')}
        </Typography>
      );
    }

    // Email + Passcode scenario
    if (requiresPasscode && requiresEmail) {
      return (
        <Typography color="textSecondary" variant="body2">
          {t('room.welcome.modal.email_passcode.subtitle')}
        </Typography>
      );
    }

    // Email-only scenario
    if (requiresEmail) {
      return (
        <Typography color="textSecondary" variant="body2">
          {t('room.contact.modal.title')}
        </Typography>
      );
    }

    if (owner) {
      return (
        <Typography color="textSecondary" variant="body2">
          {t('room.welcome.modal.subtitle', {
            owner: composeFullname(owner) || composeUsername(owner),
          })}
        </Typography>
      );
    }

    return <></>;
  };

  /** Privacy note when email (or email+PIN) is collected; omitted for passcode-only rooms */
  const renderConsentDeclinedSuppression = (): JSX.Element | null => {
    const collectsEmail = !!requiresEmail || !!verifyEmail;
    if (collectsEmail) {
      return <UnifiedConsentIdentityInfoNote actionText={renderButtonText()} />;
    }
    return null;
  };

  const renderConsent = (): JSX.Element => (
    <UnifiedSecurityDialogConsent
      isConsentDeclinedSuppression={isConsentDeclinedSuppression}
      requiresPasscode={requiresPasscode}
      requiresEmail={requiresEmail}
      shouldVerifyEmail={shouldVerifyEmail()}
      buttonText={renderButtonText()}
    />
  );

  const handleDialogClose = (
    event: Record<string, unknown>,
    reason: string
  ) => {
    if (
      (reason === 'backdropClick' || reason === 'escapeKeyDown') &&
      (requiresPasscode || shouldVerifyEmail())
    ) {
      const consentType = getUnifiedConsentType();
      Analytics.track(Events.DSR_CHAT_CONSENT_CLOSED, {
        type: consentType,
      });
    }
  };

  return (
    <Dialog
      BackdropProps={{
        style: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'var(--dialog-background-color-dark)',
        },
      }}
      fullWidth
      maxWidth="sm"
      onClose={handleDialogClose}
      open={open}
    >
      {renderErrorMessage()}
      <form onSubmit={handleSubmit} tabIndex={-1} noValidate>
        <Box
          px={4}
          pt={3}
          pb={1.5}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={3}
        >
          <DialogTitle sx={{ p: 0, m: 0 }}>{renderTitle()}</DialogTitle>
          {renderSubtitle()}
        </Box>
        <Box
          px={4}
          pb={3}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-start"
          gap={2}
        >
          {renderEmail()}
          {renderPasscode()}
          {isConsentDeclinedSuppression && needsActivityDataCheckbox && (
            <>
              <UnifiedActivityDataConsentCheckbox
                checked={activityDataConsent}
                onChange={handleActivityConsentChange}
              />
              {renderConsentDeclinedSuppression()}
            </>
          )}
          {!isConsentDeclinedSuppression &&
            (requiresPasscode || shouldVerifyEmail()) &&
            renderConsent()}
        </Box>
        <Box
          px={4}
          py={2}
          height={64}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          className={classes.footer}
        >
          <ThemableButton
            disabled={loading}
            styles={{ textTransform: 'none' }}
            type="submit"
            variant="contained"
          >
            {renderButtonText()}
          </ThemableButton>
        </Box>
      </form>
    </Dialog>
  );
};

export default UnifiedSecurityDialogContent;
